import { ContentAdd, ContentDelete, ArrowSwap } from '@fs/zion-icon'
import { IconButton, Select, Button, Row, TextField } from '@fs/zion-ui'
import React, { useContext, useReducer } from 'react'

import { useTranslation } from 'react-i18next'
import {
  createAssociationRelationship,
  createTreeDataParentChildRelationship,
  createTreeDataCouple,
  getPersonName,
} from '../../../services/personService'

import { PersonContext } from '../PersonContext'
import { isAlphaNumericPersonId } from '../../../util/EntityUtil'
import useNotifications from '../../../hooks/useNotifications'

// dmc-server reports a request it deliberately refused as a 400 carrying the reason in a DmcResponse
// notification, labeled the way ServerExceptionHandler formats every exception: `Message: <text>\n`. Strip the
// label so a reason written to be read - the self-referential parent-child check added for DATA-12351, for one -
// reaches the user as a sentence rather than as a labeled dump appended to generic error text. Anything other
// than a 400 is an upstream failure with no such reason to show, and is left to the generic handling.
const getRejectionReason = (err) =>
  err.response?.status === 400 ? err.response.data?.notifications?.[0]?.msg?.replace(/^Message:\s*/, '').trim() : null

export default function AddRelationships({ relationshipType, parent1, parent2 }) {
  const { currentPerson, fetchCurrentPerson } = useContext(PersonContext)
  const { t } = useTranslation()
  const { error } = useNotifications()
  const initialState = {
    addRelationshipOpen: false,
    associationTypeOption: '',
    personIds: { personId1: null, personId2: null },
    personNames: { personName1: null, personName2: null },
    helpMessages: { helpMessage1: null, helpMessage2: null },
    swappedAssociationRole: false,
  }

  const getOtherKey = (key) => {
    if (key === '1') {
      return '2'
    }
    return '1'
  }

  const reducer = (state, action) => {
    switch (action.type) {
      case 'TOGGLE_OPEN':
        return { ...initialState, addRelationshipOpen: !state.addRelationshipOpen }
      case 'CHANGE_ASSOCIATION_OPTION':
        return { ...state, associationTypeOption: action.value }
      case 'SET_PERSONS':
        return {
          ...state,
          personIds: action.personIds,
          personNames: action.personNames,
        }
      case `SET_PERSON_ID`: {
        const otherKey = getOtherKey(action.key)
        return {
          ...state,
          personIds: {
            [`personId${action.key}`]: action.personId,
            [`personId${otherKey}`]: otherKey === '1' ? state.personIds.personId1 : state.personIds.personId2,
          },
        }
      }
      case `SET_PERSON_NAME`: {
        const otherKey = getOtherKey(action.key)
        return {
          ...state,
          personNames: {
            [`personName${action.key}`]: action.personName,
            [`personName${otherKey}`]: otherKey === '1' ? state.personNames.personName1 : state.personNames.personName2,
          },
        }
      }
      case `SET_HELP_MESSAGE`: {
        const otherKey = getOtherKey(action.key)
        return {
          ...state,
          helpMessages: {
            [`helpMessage${action.key}`]: action.helpMessage,
            [`helpMessage${otherKey}`]:
              otherKey === '1' ? state.helpMessages.helpMessage1 : state.helpMessages.helpMessage2,
          },
        }
      }
      case 'SWAP_ASSOCIATION':
        return {
          ...state,
          personIds: { personId1: state.personIds.personId2, personId2: state.personIds.personId1 },
          personNames: { personName1: state.personNames.personName2, personName2: state.personNames.personName1 },
          helpMessages: {
            helpMessage1: state.helpMessages.helpMessage2,
            helpMessage2: state.helpMessages.helpMessage1,
          },
          swappedAssociationRole: !state.swappedAssociationRole,
        }
      default:
        return state
    }
  }
  const [state, dispatch] = useReducer(reducer, initialState)

  const ASSOCIATION = 'association'
  const PARENTS = 'parents'
  const SPOUSES = 'spouses'
  const CHILDREN = 'children'

  let relatedParent1 = null
  let relatedParent2 = null

  if (state.personIds?.personId1 === null && relationshipType === ASSOCIATION) {
    dispatch({
      type: 'SET_PERSONS',
      personIds: { personId1: currentPerson.id, personId2: null },
      personNames: { personName1: currentPerson.name, personName2: null },
    })
  }

  if (parent2) {
    parent2.id = parent2.personId
  }
  if (parent1?.gender === 'MALE') {
    relatedParent1 = parent1
    relatedParent2 = parent2
  } else {
    relatedParent1 = parent2
    relatedParent2 = parent1
  }

  const relationshipTypeMap = new Map()
  relationshipTypeMap.set(PARENTS, {
    option1: t(`relationships.labels.parents.1`, `Parent 1 Id (Father)`),
    option2: t(`relationships.labels.parents.2`, `Parent 2 Id (Mother)`),
    postRelationship: createTreeDataParentChildRelationship,
    args: [currentPerson.id, state.personIds.personId1, state.personIds.personId2],
    valid: state.personIds.personId1 || state.personIds.personId2,
  })
  relationshipTypeMap.set(CHILDREN, {
    option1: t(`relationships.labels.childId`, `Child Id`),
    postRelationship: createTreeDataParentChildRelationship,
    args: [state.personIds.personId1, relatedParent1?.id, relatedParent2?.id],
    valid: state.personIds.personId1,
  })
  relationshipTypeMap.set(SPOUSES, {
    option1: t(`relationships.labels.spouseId`, `Spouse Id`),
    postRelationship: createTreeDataCouple,
    args: [currentPerson.id, state.personIds.personId1],
    valid: state.personIds.personId1,
  })
  relationshipTypeMap.set(ASSOCIATION, {
    option1: t(`relationships.labels.association`, `Association Id`),
    option2: t(`relationships.labels.association`, `Association Id`),
    postRelationship: createAssociationRelationship,
    args: [state.associationTypeOption, state.personIds.personId1, state.personIds.personId2],
    valid: state.associationTypeOption !== '' && state.personIds.personId2,
  })

  const associationOptionsMap = new Map()
  associationOptionsMap.set('MASTER_TO_APPRENTICE', {
    label: t('relationships.association.type.apprenticeship', 'Apprenticeship'),
    option1: t('relationships.association.type.apprenticeship.master', 'Master'),
    option2: t('relationships.association.type.apprenticeship.apprentice', 'Apprentice'),
  })
  associationOptionsMap.set('EMPLOYER_TO_EMPLOYEE', {
    label: t('relationships.association.type.employment', 'Employment'),
    option1: t('relationships.association.type.employement.employer', 'Employer'),
    option2: t('relationships.association.type.employement.employee', 'Employee'),
  })
  associationOptionsMap.set('SLAVEHOLDER_TO_ENSLAVED_PERSON', {
    label: t('relationships.association.type.enslavement', 'Enslavement'),
    option1: t('relationships.association.type.enslavement.slaveholder', 'Slaveholder'),
    option2: t('relationships.association.type.enslavement.enslavedPerson', 'Enslaved Person'),
  })
  associationOptionsMap.set('GODPARENT_TO_GODCHILD', {
    label: t('relationships.association.type.godparent', 'Godparent'),
    option1: t('relationships.association.type.godparent.godparent', 'Godparent'),
    option2: t('relationships.association.type.godparent.godchild', 'Godchild'),
  })
  associationOptionsMap.set('HEAD_OF_HOUSEHOLD_TO_OCCUPANT', {
    label: t('relationships.association.type.household', 'Household'),
    option1: t('relationships.association.type.household.headOfHousehold', 'Head of Household'),
    option2: t('relationships.association.type.household.occupant', 'Occupant'),
  })
  associationOptionsMap.set('NEIGHBOR_TO_NEIGHBOR', {
    label: t('relationships.association.type.neighbor', 'Neighbor'),
    option1: t('relationships.association.type.neighbor.neighbor', 'Neighbor'),
    option2: t('relationships.association.type.neighbor.neighbor', 'Neighbor'),
  })
  associationOptionsMap.set('RELATIVE_TO_RELATIVE', {
    label: t('relationships.association.type.relative', 'Relative'),
    option1: t('relationships.association.type.relative.relative', 'Relative'),
    option2: t('relationships.association.type.relative.relative', 'Relative'),
  })

  const errorsMap = new Map()
  errorsMap.set('400', t('relationships.errors.400', 'Please Enter a Valid ID'))
  errorsMap.set('403', t('relationships.errors.403', 'Person is view-restricted from user'))
  errorsMap.set('404', t('relationships.errors.404', 'Person with the given ID is not found'))
  errorsMap.set('410', t('relationships.errors.410', 'Person with the given ID is tombstoned'))

  const setInputPersonIds = async (value, personKey) => {
    value = value.toUpperCase()
    if (value.substring(0, 4) === 'PID.') {
      value = value.substring(4)
    }
    if (value.substring(4, 5) !== '-' && value.substring(4, 5) !== '') {
      value = `${value.substring(0, 4)}-${value.substring(4)}`
    }
    dispatch({ type: `SET_PERSON_ID`, personId: value.trim(), key: personKey })
    if (isAlphaNumericPersonId(value.trim())) {
      let personName = null
      try {
        personName = await getPersonName(value)
        dispatch({ type: `SET_PERSON_NAME`, personName, key: personKey })
      } catch (e) {
        dispatch({ type: `SET_PERSON_NAME`, personName: null, key: personKey })
        dispatch({ type: `SET_HELP_MESSAGE`, helpMessage: errorsMap.get(`${e.response.status}`), key: personKey })
      }
    } else {
      dispatch({ type: 'SET_PERSON_NAME', personName: null, key: personKey })
      dispatch({ type: 'SET_HELP_MESSAGE', helpMessage: null, key: personKey })
    }
  }

  const saveRelationship = () => {
    relationshipTypeMap
      .get(relationshipType)
      .postRelationship(...relationshipTypeMap.get(relationshipType).args)
      .then((response) => {
        if (relationshipType === ASSOCIATION) {
          fetchCurrentPerson()
        }
        if (response.data?.status !== 'OK') {
          response.data?.statuses?.forEach((status) => {
            status.message && error(`${t('relationships.add.error', 'Error')}: ${status.message}`)
          })
        } else {
          fetchCurrentPerson()
        }
      })
      .catch((err) => {
        const rejectionReason = getRejectionReason(err)
        if (rejectionReason) {
          error(rejectionReason)
          return
        }
        error(t('relationships.error.errorCreatingRelationship', 'Error creating relationship'), err)
      })
  }

  const getAssociationOptions = () => {
    const typeList = []
    associationOptionsMap.forEach((value, key) => {
      typeList.push({ label: value.label, value: key })
    })
    return typeList
  }

  return (
    <>
      {state.addRelationshipOpen && (
        <>
          <IconButton
            Icon={ContentDelete}
            color="red"
            onClick={() => dispatch({ type: 'TOGGLE_OPEN' })}
            emphasis="high"
            data-testid={`${relationshipType}_delete_button`}
          />
          <Row>
            {((relationshipType === ASSOCIATION && state.swappedAssociationRole) ||
              relationshipType !== ASSOCIATION) && (
              <TextField
                id={`input_${relationshipType}_1`}
                label={relationshipTypeMap.get(relationshipType)?.option1}
                type="text"
                value={state.personIds.personId1}
                onChange={(e) => setInputPersonIds(e.target.value, '1')}
                validMessage={state.personNames.personName1}
                helpMessage={state.helpMessages.helpMessage1}
              />
            )}
            {((relationshipType === ASSOCIATION && !state.swappedAssociationRole) || relationshipType === PARENTS) && (
              <TextField
                id={`input_${relationshipType}_2`}
                label={relationshipTypeMap.get(relationshipType)?.option2}
                type="text"
                value={state.personIds.personId2}
                onChange={(e) => setInputPersonIds(e.target.value, '2')}
                validMessage={state.personNames.personName2}
                helpMessage={state.helpMessages.helpMessage2}
              />
            )}
          </Row>

          {relationshipType === ASSOCIATION && (
            <>
              <Row>
                <Select
                  id="selectAssociationType"
                  placeholder={t('relationships.associations.types.selectType', 'Select Relationship Type')}
                  value={state.associationTypeOption}
                  onChange={(e) => dispatch({ type: 'CHANGE_ASSOCIATION_OPTION', value: e.target.value })}
                  options={getAssociationOptions()}
                />
              </Row>
              {relationshipTypeMap.get(ASSOCIATION).valid && (
                <Row>
                  <IconButton
                    Icon={ArrowSwap}
                    onClick={() => dispatch({ type: 'SWAP_ASSOCIATION' })}
                    data-testid="swap_button"
                  />
                  <>
                    <div>
                      {associationOptionsMap.get(state.associationTypeOption).option1} - {state.personIds?.personId1} (
                      {state.personNames.personName1})
                    </div>
                    <div>
                      {associationOptionsMap.get(state.associationTypeOption).option2} - {state.personIds?.personId2} (
                      {state.personNames.personName2})
                    </div>
                  </>
                </Row>
              )}
            </>
          )}
          <Button
            emphasis="high"
            onClick={saveRelationship}
            disabled={!relationshipTypeMap.get(relationshipType).valid}
            data-testid={`save_${relationshipType}_button`}
            id={`save_${relationshipType}_button`}
          >
            {t('relationships.saveRelationship', 'Save Relationship')}
          </Button>
        </>
      )}
      {!state.addRelationshipOpen && (
        <IconButton
          Icon={ContentAdd}
          color="green"
          onClick={() => dispatch({ type: 'TOGGLE_OPEN' })}
          emphasis="high"
          data-testid={`${relationshipType}_add_button`}
          id={`${relationshipType}_add_button`}
        />
      )}
    </>
  )
}
